/**
 * Math subject focus API client — the student's own read/write of
 * `User.focusMathSubject` (`GET`/`PATCH /practice/focus-subject`).
 *
 * Focus, not filter. `/progress` has carried an ephemeral component-local
 * All/E-Math/A-Math toggle for a while; it changed what you browsed and
 * nothing about what got served. The durable value lives on the User row, so
 * "E-Math is over, only A-Math now" is true on every device and every surface.
 *
 * Self-only by construction: neither call takes a user id. Staff previewing a
 * cohort go through the "Try out as" selector, which is request-scoped and
 * never persisted, and staff viewing a named student's `/progress` keep a
 * local, ephemeral pick that never reaches this module.
 *
 * One paper is always in force where the split exists. The GET returns the
 * paper the server resolved (the student's choice, else E-Math when they hold
 * it, else the single paper they hold) and whether it was chosen; the PATCH
 * names a paper and the server rejects `null`.
 */
import type { MathSubject } from '@shared/mathSubjects';

import { fetchWithConfig } from './apiUtils';

export interface MathSubjectFocusResponse {
  /**
   * The paper in force for the actor, resolved server-side: their choice, else
   * the default paper. `null` only where no paper applies (no split at their
   * level, or no paper held).
   */
  focusMathSubject: MathSubject | null;
  /** True when the student chose the paper; false when the default supplied it. */
  explicit?: boolean;
  /** Enrolment truth (`User.mathSubjects`), verbatim — never derived. */
  mathSubjects: MathSubject[];
  /**
   * Whether the E-Math/A-Math split exists at all at this student's level —
   * the server's evaluation of `isMathSubjectApplicable(examLevel, orgLevel)`
   * against the actor's own profile. The split is real only in Sec 3/4 at
   * G2/G3; everywhere else Mathematics is one subject.
   */
  applicable: boolean;
}

export interface MathSubjectFocusUpdateResponse {
  focusMathSubject: MathSubject;
}

const ENDPOINT = '/practice/focus-subject';

export const mathSubjectFocusApi = {
  async get(token: string): Promise<MathSubjectFocusResponse> {
    return fetchWithConfig<MathSubjectFocusResponse>(ENDPOINT, {
      method: 'GET',
      authToken: token,
    });
  },

  /**
   * Writes the actor's own focus. `422 subject_not_enrolled` /
   * `422 subject_not_applicable` are the server re-checking the same two facts
   * the control gates on. The caller keeps its prior value authoritative until
   * this request succeeds; a rejection prompts a fresh read because the
   * student's enrolment or level may have changed.
   */
  async update(
    mathSubject: MathSubject,
    token: string
  ): Promise<MathSubjectFocusUpdateResponse> {
    return fetchWithConfig<MathSubjectFocusUpdateResponse>(ENDPOINT, {
      method: 'PATCH',
      authToken: token,
      body: JSON.stringify({ mathSubject }),
    });
  },
};
